import React, { createContext, useContext } from 'react';

/**
 * Staff cost views show raw model cost (no customer multiplier) in SEK, in the
 * same format as the customer Cost page. The API keeps costs in USD and sends
 * the one USD to SEK rate (`sekPerUsd`, from the backend's USD_SEK_RATE).
 */
const TAG = 'en-GB';

export const formatSek = (sek: number, compact = false): string => {
  if (compact) {
    if (sek === 0) return '0';
    if (sek >= 1000) return `${(sek / 1000).toFixed(sek >= 10_000 ? 0 : 1).replace(/\.0$/, '')}k`;
    if (sek >= 1) return sek.toFixed(sek >= 10 ? 0 : 1).replace(/\.0$/, '');
    return String(Number(sek.toPrecision(2)));
  }
  const digits = sek >= 100 ? 0 : sek >= 1 || sek === 0 ? 2 : 4;
  return new Intl.NumberFormat(TAG, {
    style: 'currency',
    currency: 'SEK',
    minimumFractionDigits: Math.min(2, digits),
    maximumFractionDigits: digits,
  }).format(sek);
};

export const formatUsdTitle = (usd: number): string =>
  `$${usd.toLocaleString('en-US', { maximumFractionDigits: 4 })} USD`;

const RateContext = createContext<number | null>(null);

export const SekRateProvider: React.FC<{ rate: number; children: React.ReactNode }> = ({ rate, children }) => (
  <RateContext.Provider value={rate}>{children}</RateContext.Provider>
);

/** `(usd, compact?) => "SEK 12.34"`; must sit under a SekRateProvider */
export const useSek = () => {
  const rate = useContext(RateContext);
  if (rate === null) throw new Error('useSek needs a SekRateProvider');
  return (usd: number, compact = false) => formatSek(usd * rate, compact);
};
